import { Div, type StyledSystemProps, useStyledSystem } from "@hopper-ui/components";
import { ProgressBar } from "react-aria-components";
import { BRAND_GRADIENT_FILL } from "../charts/dataVizGradients.ts";
import styles from "./Progress.module.css";

interface ProgressProps {
    "aria-label": string;
    value?: number;
    max?: number;
    width?: StyledSystemProps["width"];
    fillColor?: StyledSystemProps["backgroundColor"];
    isGradient?: boolean;
    valueLabel?: string;
    isIndeterminate?: boolean;
}

export function Progress({
    value = 0,
    max = 100,
    width = "100%",
    fillColor = "neutral-strong",
    isGradient = false,
    valueLabel,
    "aria-label": ariaLabel,
    isIndeterminate = false
}: ProgressProps) {
    const safeMax = max <= 0 ? 100 : max;
    const percentage = Math.max(0, Math.min(100, Math.round((value / safeMax) * 100)));

    const { stylingProps } = useStyledSystem({
        backgroundColor: "neutral-weak",
        UNSAFE_height: "0.375rem",
        borderRadius: "circle",
        width: "100%",
        overflow: "hidden"
    });

    return (
        <Div width={width}>
            <ProgressBar
                value={value}
                minValue={0}
                maxValue={safeMax}
                aria-label={ariaLabel}
                valueLabel={valueLabel}
                isIndeterminate={isIndeterminate}
                {...stylingProps}
            >
                <Div
                    height="100%"
                    backgroundColor={isGradient ? undefined : fillColor}
                    backgroundImage={isGradient ? BRAND_GRADIENT_FILL : undefined}
                    backgroundPosition="left center"
                    // Stretch the gradient across the full track so a partial fill shows only its start.
                    backgroundSize={isGradient && percentage > 0 ? `${(100 / percentage) * 100}% 100%` : undefined}
                    borderRadius="circle"
                    className={isIndeterminate ? styles.indeterminate : undefined}
                    style={isIndeterminate ? undefined : { width: `${percentage}%` }}
                    transition="width var(--hop-easing-duration-3) var(--hop-easing-focus)"
                />
            </ProgressBar>
        </Div>
    );
}
